import React, { useState, useCallback } from 'react';
import { Box } from '@mui/material';
import Image from 'next/image';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import type SwiperClass from 'swiper/types/swiper-class';
import { spacing, semanticColors, containerSizes } from 'design-system';
import { shouldEagerLoad } from './preload-policy';
import { CAROUSEL_SHIMMER } from './shimmer';

import 'swiper/css';
import 'swiper/css/pagination';

/**
 * ImageCarousel Props
 */
export interface ImageCarouselProps {
  /** Array of image URLs */
  images: string[];
  
  /** Alt text prefix for images */
  altPrefix: string;
  
  /** Image descriptions for accessibility (Map of index -> description) */
  imageDescriptions?: Map<string, string> | null;
  
  /** Height of the carousel */
  height?: number | string;
  
  /** Enable priority loading for first image (LCP optimization) */
  priority?: boolean;
  
  /** Called when slide changes */
  onSlideChange?: (index: number) => void;
  
  /** Called when image is clicked */
  onImageClick?: () => void;
  
  /** Current slide index (controlled) */
  currentIndex?: number;
  
  /** Overlay content (badges, counter) - rendered on top of carousel */
  children?: React.ReactNode;
}

/**
 * ImageCarousel Component
 * 
 * A swipeable image carousel built on Swiper.js.
 * 
 * Features:
 * - Touch swipe navigation
 * - Keyboard navigation
 * - Supports overlay content (badges, counters)
 * - Controlled or uncontrolled mode
 * - Smart preloading (see PRELOAD_POLICY)
 * 
 * @example
 * ```tsx
 * <ImageCarousel 
 *   images={['img1.jpg', 'img2.jpg']} 
 *   altPrefix="123 Main St"
 *   onSlideChange={(i) => console.log(i)}
 * >
 *   <BadgeGroup position="top-left">...</BadgeGroup>
 *   <ImageCounter current={1} total={10} />
 * </ImageCarousel>
 * ```
 */
export default function ImageCarousel({
  images,
  altPrefix,
  imageDescriptions,
  height = containerSizes.gallery.mobile,
  priority = false,
  onSlideChange,
  onImageClick,
  currentIndex,
  children,
}: ImageCarouselProps) {
  const [internalIndex, setInternalIndex] = useState(0);
  const [hasInteracted, setHasInteracted] = useState(false);
  const activeIndex = currentIndex ?? internalIndex;

  const handleSlideChange = useCallback((swiper: SwiperClass) => {
    const newIndex = swiper.realIndex;
    setInternalIndex(newIndex);
    setHasInteracted(true);
    onSlideChange?.(newIndex);
  }, [onSlideChange]);

  const getImageAlt = (index: number): string => {
    const indexStr = index.toString();
    if (imageDescriptions && imageDescriptions.has(indexStr)) {
      return `${imageDescriptions.get(indexStr)} | Image ${index + 1}`;
    }
    return `${altPrefix} | Image ${index + 1}`;
  };

  return (
    <Box
      sx={{ 
        position: 'relative', 
        width: '100%',
        '& .swiper-pagination': {
          bottom: `${spacing.lg} !important`,
        },
        '& .swiper-pagination-bullet': {
          width: `${containerSizes.paginationBullet} !important`,
          height: `${containerSizes.paginationBullet} !important`,
          backgroundColor: `${semanticColors.pagination} !important`,
          opacity: '0.5 !important',
          margin: `0 ${spacing.sm} !important`,
        },
        '& .swiper-pagination-bullet-active': {
          backgroundColor: `${semanticColors.pagination} !important`,
          opacity: '1 !important',
        },
      }}
    >
      <Swiper
        modules={[Pagination]}
        spaceBetween={0}
        slidesPerView={1}
        pagination={{
          clickable: true,
          dynamicBullets: true,
          dynamicMainBullets: 3,
        }}
        onSlideChange={handleSlideChange}
        initialSlide={activeIndex}
        style={{ height: typeof height === 'number' ? `${height}px` : height }}
        watchSlidesProgress={true}
      >
        {images.map((url, index) => {
          const eagerLoad = shouldEagerLoad(index, activeIndex, hasInteracted, index === 0);

          return (
            <SwiperSlide key={index}>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  cursor: onImageClick ? 'pointer' : 'default',
                }}
                onClick={onImageClick}
              >
                <Image
                  src={url}
                  alt={getImageAlt(index)}
                  fill
                  sizes="(max-width: 639px) 100vw, 640px"
                  style={{
                    objectFit: 'cover',
                    userSelect: 'none',
                  }}
                  priority={index === 0 && priority}
                  loading={eagerLoad ? 'eager' : 'lazy'}
                  placeholder="blur"
                  blurDataURL={CAROUSEL_SHIMMER}
                />
              </Box>
            </SwiperSlide>
          );
        })}
      </Swiper>
      {children}
    </Box>
  );
}
